/**
 * Analytics module — sends events to both Google Analytics 4 and custom backend.
 *
 * GA4 handles: traffic sources, devices, geography, bounce rate, session duration.
 * Custom backend handles: registration funnel, per-subdomain metrics, admin dashboard cards.
 */

// Extend window for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

// ─── Session ID (persists per browser tab) ───────────────────────────────────

function getSessionId(): string {
  let sid = sessionStorage.getItem('_ovrwatch_sid')
  if (!sid) {
    sid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem('_ovrwatch_sid', sid)
  }
  return sid
}

function getSubdomain(): string {
  const host = window.location.hostname
  if (host.includes('.ovrwatch.app')) {
    return host.split('.')[0].toLowerCase()
  }
  return 'localhost'
}

// ─── Send to GA4 ─────────────────────────────────────────────────────────────

function sendToGA4(eventName: string, params?: Record<string, string>) {
  if (window.gtag) {
    window.gtag('event', eventName, {
      ...params,
      subdomain: getSubdomain(),
    })
  }
}

// ─── Send to Custom Backend ──────────────────────────────────────────────────

function sendToBackend(eventType: string, metadata?: Record<string, string>) {
  // Fire and forget — don't await, don't block UI
  const payload = {
    event_type: eventType,
    page_path: window.location.pathname,
    subdomain: getSubdomain(),
    referrer: document.referrer || null,
    session_id: getSessionId(),
    metadata: metadata || {},
  }

  fetch('/api/analytics/track', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true, // Survives page unload
  }).catch(() => {
    // Silently ignore tracking failures — never break the app
  })
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Track a page view (GA4 + backend).
 * Call this once per page/route.
 */
export function trackPageView(pagePath?: string) {
  const path = pagePath || window.location.pathname
  sendToGA4('page_view', { page_path: path })
  sendToBackend('page_view')
}

/**
 * Track resident login page view.
 */
export function trackLoginPageView() {
  sendToGA4('login_page_view', { page_path: '/login' })
  sendToBackend('login_page_view')
}

/**
 * Track admin login page view.
 */
export function trackAdminLoginPageView() {
  sendToGA4('admin_login_page_view', { page_path: '/admin/login' })
  sendToBackend('admin_login_page_view')
}

/**
 * Track first-time setup flow started.
 */
export function trackFirstTimeStart() {
  sendToGA4('first_time_start')
  sendToBackend('first_time_start')
}

/**
 * Track first-time setup completed successfully.
 */
export function trackFirstTimeComplete() {
  sendToGA4('first_time_complete')
  sendToBackend('first_time_complete')
}

/**
 * Track returning user login started (clicked button).
 */
export function trackReturningLoginStart() {
  sendToGA4('returning_login_start')
  sendToBackend('returning_login_start')
}

/**
 * Track returning user login completed successfully.
 */
export function trackReturningLoginComplete() {
  sendToGA4('returning_login_complete')
  sendToBackend('returning_login_complete')
}

/**
 * Track self-registration modal opened.
 */
export function trackSelfRegisterStart() {
  sendToGA4('self_register_start')
  sendToBackend('self_register_start')
}

/**
 * Track self-registration completed successfully.
 */
export function trackSelfRegisterComplete() {
  sendToGA4('self_register_complete')
  sendToBackend('self_register_complete')
}
